import { useEffect, useMemo, useRef, useState } from "react";
import { PinInput, PinInputField, HStack, useToast } from "@chakra-ui/react";
import { ArrowLeftOutlined, PhoneOutlined } from "@ant-design/icons";
import axios from "axios";
import { Helmet } from "react-helmet";
import PhoneInput, {
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import ru from "react-phone-number-input/locale/ru";

import "./aouth.css";
import "./aouthDark.css";
import logosvg from "../../../img/LogoUSvg.svg";


import sockets from "../../../socket/socket";
import LendingQrScannModalBTN from "../lendingQrScannModal/lendingQrScannModal";
import OfferModal from "../offerModal/offerModal";
import { appendReferralCodeToPayload, captureReferralCodeFromLocation } from "../referral/referralStorage";
import { appendAttributionToPayload } from "../referral/attributionStorage";
import { METRIKA_GOALS, trackMetrikaGoal } from "../../utilt/bootstrapAlert/yandexMetrica";
import Landing from "../landing/Landing";
import { getPendingPublicCalcPath } from "../../page/unitPlanning/pendingPublicCalc";
import { callWindowText, groupPhone, phoneStepText } from "./authTexts";
import styles from "./AuthCard.module.css";
import useBrowserTheme from "../../ui/v3/useBrowserTheme";
import shotUnit from "../landing/shots/unit.webp";
import shotUnitDark from "../landing/shots/unit-dark.webp";
import shotTasks from "../landing/shots/tasks.webp";
import shotTasksDark from "../landing/shots/tasks-dark.webp";
import shotOrders from "../landing/shots/orders.webp";
import shotOrdersDark from "../landing/shots/orders-dark.webp";

const socket = sockets;
const cx = (...names) => names.filter(Boolean).join(" ");
const LEGAL_EMAIL = "uprosti.rf@yandex.ru";
const LEGAL_PHONE = "+7 930 999-30-89";
const LEGAL_DOCUMENTS = {
  offer: "/documents/public-offer.pdf",
  privacy: "/documents/privacy-policy.pdf",
  consent: "/documents/personal-data-consent.pdf",
  details: "/documents/company-details.pdf",
};

const LEGAL_INFO = {
  owner: "ИП Ермаков Александр Геннадьевич",
  email: LEGAL_EMAIL,
  phone: LEGAL_PHONE,
  telegram: "https://t.me/uprost",
  documents: LEGAL_DOCUMENTS,
};

// Скриншоты сервиса на демо-данных для блока «Так выглядит сервис»: WebP 1280 px из PNG рядом.
const LANDING_SHOTS = {
  unit: shotUnit,
  unitDark: shotUnitDark,
  tasks: shotTasks,
  tasksDark: shotTasksDark,
  orders: shotOrders,
  ordersDark: shotOrdersDark,
};

const AUTH_REDIRECT_PATHS = {
  createCompany: "/add-company",
  connections: "/connections",
  dashboard: "/",
};

function notifySocketBeforeRedirect(userId) {
  return new Promise((resolve) => {
    if (!socket || !userId) {
      resolve();
      return;
    }

    let finished = false;

    const finish = () => {
      if (finished) return;
      finished = true;
      resolve();
    };

    const emitUpdates = () => {
      try {
        socket.emit("pushmyid", userId);
        socket.emit("updateMydataOnlineTrueForServer");
        socket.emit("activeSessions:refreshForUser", {}, finish);
        setTimeout(finish, 350);
      } catch (error) {
        console.error("Ошибка socket обновления перед редиректом:", error);
        finish();
      }
    };

    try {
      if (socket.connected) {
        emitUpdates();
        return;
      }

      socket.once("connect", emitUpdates);

      if (typeof socket.connect === "function") {
        socket.connect();
      }

      setTimeout(finish, 600);
    } catch (error) {
      console.error("Ошибка socket connect перед редиректом:", error);
      finish();
    }
  });
}
const AUTH_AUTO_DARK_STYLE_ID = "uprasti-auth-auto-dark-style";
const SYSTEM_DARK_THEME_QUERY = "(prefers-color-scheme: dark)";

const AUTH_AUTO_DARK_CSS = `
body.uprasti-auth-dark {
  background: #0f172a !important;
}

.uprastiLandingPage.isDark {
  --auth-bg: #0f172a;
  --auth-card: rgba(15, 23, 42, 0.94);
  --auth-border: rgba(148, 163, 184, 0.18);
  --auth-text: #f8fafc;
  --auth-muted: #cbd5e1;
  --auth-muted-2: #94a3b8;

  background: #0f172a !important;
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .landingHero {
  background:
    radial-gradient(circle at 15% 20%, rgba(59, 130, 246, 0.22), transparent 32%),
    radial-gradient(circle at 85% 15%, rgba(6, 182, 212, 0.18), transparent 30%),
    linear-gradient(180deg, #0f172a 0%, #111827 72%) !important;
}

.uprastiLandingPage.isDark .landingHeader,
.uprastiLandingPage.isDark .landingHeader.isScrolled {
  background: rgba(15, 23, 42, 0.64) !important;
  border-bottom-color: rgba(148, 163, 184, 0.16) !important;
  box-shadow: 0 12px 34px rgba(0, 0, 0, 0.22) !important;
}

.uprastiLandingPage.isDark .landingLogo b,
.uprastiLandingPage.isDark .landingHeroText h1,
.uprastiLandingPage.isDark .authTitleBlock p:first-child,
.uprastiLandingPage.isDark .landingSectionHead h2,
.uprastiLandingPage.isDark .landingFeatureCard h3,
.uprastiLandingPage.isDark .landingStepCard h3,
.uprastiLandingPage.isDark .landingRecommendationCard h3,
.uprastiLandingPage.isDark .landingTariffBand h2,
.uprastiLandingPage.isDark .landingTariffBand b,
.uprastiLandingPage.isDark .landingTasksBand h2,
.uprastiLandingPage.isDark .landingFooterInner b {
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .landingLogo em,
.uprastiLandingPage.isDark .landingHeroText p,
.uprastiLandingPage.isDark .authTitleBlock p:last-child,
.uprastiLandingPage.isDark .landingSectionHead p,
.uprastiLandingPage.isDark .landingFeatureCard p,
.uprastiLandingPage.isDark .landingStepCard p,
.uprastiLandingPage.isDark .landingRecommendationCard p,
.uprastiLandingPage.isDark .landingTariffBand p,
.uprastiLandingPage.isDark .landingFooter p,
.uprastiLandingPage.isDark .landingFooter span {
  color: #cbd5e1 !important;
}

.uprastiLandingPage.isDark .landingNav {
  color: #cbd5e1 !important;
}

.uprastiLandingPage.isDark .landingLoginButton,
.uprastiLandingPage.isDark .landingSecondaryButton,
.uprastiLandingPage.isDark .landingHeroBadge,
.uprastiLandingPage.isDark .landingPillList span,
.uprastiLandingPage.isDark .authFormCard,
.uprastiLandingPage.isDark .landingTariffBand,
.uprastiLandingPage.isDark .landingKpiCard,
.uprastiLandingPage.isDark .landingFeatureCard,
.uprastiLandingPage.isDark .landingStepCard,
.uprastiLandingPage.isDark .landingRecommendationCard,
.uprastiLandingPage.isDark .landingWorkspacePreview,
.uprastiLandingPage.isDark .authQrLoginRow,
.uprastiLandingPage.isDark .authCodeInfoCard,
.uprastiLandingPage.isDark .landingShowcaseMain,
.uprastiLandingPage.isDark .landingHowBox {
  background: rgba(15, 23, 42, 0.88) !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
  color: #f8fafc !important;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.22) !important;
}

.uprastiLandingPage.isDark .authLogoBox {
  background:
    linear-gradient(180deg, rgba(30, 41, 59, 0.96), rgba(15, 23, 42, 0.92)),
    #0f172a !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
}

.uprastiLandingPage.isDark .verifyInput {
  background: rgba(15, 23, 42, 0.92) !important;
  border-color: rgba(96, 165, 250, 0.38) !important;
}

.uprastiLandingPage.isDark .verifyInput .PhoneInputInput {
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .verifyInput .PhoneInputInput::placeholder {
  color: #64748b !important;
}

.uprastiLandingPage.isDark .authFieldLabel,
.uprastiLandingPage.isDark .authHintText,
.uprastiLandingPage.isDark .authAgreementText,
.uprastiLandingPage.isDark .authQrLoginText span {
  color: #cbd5e1 !important;
}

.uprastiLandingPage.isDark .authQrLoginText p,
.uprastiLandingPage.isDark .authCodeInfoCard p {
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .authDivider {
  color: #94a3b8 !important;
}

.uprastiLandingPage.isDark .authDivider span {
  background: rgba(148, 163, 184, 0.24) !important;
}

.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary:disabled,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary.ant-btn-disabled,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary[disabled] {
  background: rgba(30, 41, 59, 0.96) !important;
  border-color: rgba(148, 163, 184, 0.22) !important;
  color: #94a3b8 !important;
  box-shadow: none !important;
  opacity: 1 !important;
}

.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary:disabled span,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary.ant-btn-disabled span,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary[disabled] span,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary:disabled .anticon,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary.ant-btn-disabled .anticon,
.uprastiLandingPage.isDark .authPrimaryButton.ant-btn-primary[disabled] .anticon {
  color: #94a3b8 !important;
}

.uprastiLandingPage.isDark .landingTasksBand {
  background:
    radial-gradient(circle at 12% 18%, rgba(37, 99, 235, 0.22), transparent 32%),
    radial-gradient(circle at 88% 12%, rgba(6, 182, 212, 0.18), transparent 30%),
    linear-gradient(135deg, #111827 0%, #0f172a 100%) !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
}

.uprastiLandingPage.isDark .landingTasksBand p,
.uprastiLandingPage.isDark .landingTaskPreviewRow p {
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .landingDarkPills b,
.uprastiLandingPage.isDark .landingTaskPreviewPanel,
.uprastiLandingPage.isDark .landingTaskPreviewRow {
  background: rgba(15, 23, 42, 0.78) !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
  color: #f8fafc !important;
}

.uprastiLandingPage.isDark .landingFooter {
  background: #0f172a !important;
  border-top-color: rgba(148, 163, 184, 0.18) !important;
}

body.uprasti-auth-dark .callbackRequestModal .ant-modal-content {
  background:
    radial-gradient(circle at 12% 10%, rgba(37, 99, 235, 0.18), transparent 28%),
    radial-gradient(circle at 92% 0%, rgba(6, 182, 212, 0.16), transparent 32%),
    #0f172a !important;
  border-color: rgba(148, 163, 184, 0.18) !important;
}

body.uprasti-auth-dark .callbackRequestHead h3,
body.uprasti-auth-dark .callbackRequestField > p {
  color: #f8fafc !important;
}

body.uprasti-auth-dark .callbackRequestHead p,
body.uprasti-auth-dark .callbackRequestField > span {
  color: #cbd5e1 !important;
}
`;



function getSystemPrefersDarkTheme() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia(SYSTEM_DARK_THEME_QUERY).matches;
}

function applyAuthThemeToDocument(nextIsDark) {
  setAuthAutoDarkCss(nextIsDark);

  if (typeof document !== "undefined") {
    document.body.classList.toggle("uprasti-auth-dark", nextIsDark);
    document.body.classList.toggle("dark", nextIsDark);
    document.documentElement.setAttribute("data-theme", nextIsDark ? "dark" : "light");
  }
}

function setAuthAutoDarkCss(enabled) {
  if (typeof document === "undefined") return;

  const oldStyle = document.getElementById(AUTH_AUTO_DARK_STYLE_ID);

  if (!enabled) {
    oldStyle?.remove();
    return;
  }

  if (oldStyle) return;

  const style = document.createElement("style");
  style.id = AUTH_AUTO_DARK_STYLE_ID;
  style.textContent = AUTH_AUTO_DARK_CSS;
  document.head.appendChild(style);
}


const DEFAULT_PHONE_COUNTRY = "RU";

const COUNTRY_BY_TIMEZONE = {
  "Asia/Yerevan": "AM",
  "Europe/Moscow": "RU",
  "Europe/Kaliningrad": "RU",
  "Europe/Samara": "RU",
  "Asia/Yekaterinburg": "RU",
  "Asia/Omsk": "RU",
  "Asia/Novosibirsk": "RU",
  "Asia/Barnaul": "RU",
  "Asia/Tomsk": "RU",
  "Asia/Krasnoyarsk": "RU",
  "Asia/Irkutsk": "RU",
  "Asia/Chita": "RU",
  "Asia/Yakutsk": "RU",
  "Asia/Vladivostok": "RU",
  "Asia/Magadan": "RU",
  "Asia/Sakhalin": "RU",
  "Asia/Kamchatka": "RU",
};

function normalizeCountryCode(countryCode) {
  const nextCountry = String(countryCode || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

  return nextCountry.length === 2 ? nextCountry : "";
}

function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (error) {
    return "";
  }
}

function getCountryFromBrowserLanguage() {
  if (typeof navigator === "undefined") return "";

  const languages = [navigator.language, ...(navigator.languages || [])].filter(Boolean);

  for (const language of languages) {
    const parts = String(language).split("-");
    const countryPart = normalizeCountryCode(parts[1]);

    if (countryPart) return countryPart;
  }

  return "";
}

function guessPhoneCountry() {
  const timeZone = getBrowserTimeZone();
  const timeZoneCountry = normalizeCountryCode(COUNTRY_BY_TIMEZONE[timeZone]);

  if (timeZoneCountry) return timeZoneCountry;

  const languageCountry = getCountryFromBrowserLanguage();

  if (languageCountry) return languageCountry;

  return DEFAULT_PHONE_COUNTRY;
}

async function detectPhoneCountryFromServer() {
  try {
    const response = await axios.get("/geo/country/api", {
      withCredentials: true,
      timeout: 3500,
    });

    return normalizeCountryCode(
      response.data?.country ||
      response.data?.countryCode ||
      response.data?.code
    );
  } catch (error) {
    return "";
  }
}

async function detectPhoneCountryFromPublicApi() {
  try {
    const response = await axios.get("https://ipapi.co/json/", {
      timeout: 3500,
    });

    return normalizeCountryCode(
      response.data?.country ||
      response.data?.country_code ||
      response.data?.countryCode
    );
  } catch (error) {
    return "";
  }
}



function normalizePhoneForServer(phone, countryCode) {
  const raw = String(phone || "").trim();
  const country = normalizeCountryCode(countryCode);
  let digits = raw.replace(/\D/g, "");

  if (!digits) return "";
  if (raw.startsWith("+")) return digits;
  if (digits.startsWith("00") && digits.length > 4) return digits.slice(2);

  if (country === "RU") {
    if (digits.length === 10 && digits.startsWith("9")) return `7${digits}`;
    if (digits.length === 11 && digits.startsWith("8")) return `7${digits.slice(1)}`;
    if (digits.length === 11 && digits.startsWith("7")) return digits;
  }

  if (country === "AM") {
    if (digits.length === 8) return `374${digits}`;
    if (digits.length === 9 && digits.startsWith("0")) return `374${digits.slice(1)}`;
    if (digits.length === 11 && digits.startsWith("374")) return digits;
  }

  if (digits.length === 10 && digits.startsWith("9")) return `7${digits}`;
  if (digits.length === 11 && digits.startsWith("8")) return `7${digits.slice(1)}`;

  return digits;
}

function normalizeCallPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits ? `+${digits}` : "";
}

function formatPhoneForCall(phone) {
  const normalizedPhone = normalizeCallPhone(phone);
  if (!normalizedPhone) return "";

  return groupPhone(formatPhoneNumberIntl(normalizedPhone) || normalizedPhone);
}

export default function Outh() {
  const [isSending, setIsSending] = useState(false);
  const [inptValue, setinptValue] = useState("");
  const [loading, setloading] = useState(false);
  const [inpDisapled, setInpDisapled] = useState(false);
  const [pohoneNumber, setPohoneNumber] = useState(null);
  const [isCodeStep, setIsCodeStep] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isDark, setIsDark] = useState(() => getSystemPrefersDarkTheme());
  useBrowserTheme(isDark);
  const [country, setcountry] = useState(() => guessPhoneCountry());
  const [outgoingAuth, setOutgoingAuth] = useState(null);
  const [outgoingStatusText, setOutgoingStatusText] = useState("");
  const [isLocalPinMode, setIsLocalPinMode] = useState(false);
  // Ошибки и конец попытки показываем в самой карточке: всплывающие окна закрывали её на телефоне.
  const [authError, setAuthError] = useState("");
  const [callEnded, setCallEnded] = useState("");
  const [offerNewNumber, setOfferNewNumber] = useState(false);

  const toast = useToast({ position: "top" });
  const inputRefDesktop = useRef(null);
  const focusRefDesktop = useRef(null);
  const pincodeToSendRef = useRef("");
  // Откуда открыт вход на лендинге (кнопка «Войти», пробный период, сохранение расчёта).
  const authContextRef = useRef({});
  // Номер новый (сервер ответил 201): уходит в цель «вход выполнен» как new_user.
  const isNewUserRef = useRef(false);

  const trackLoginSuccess = (method) => {
    trackMetrikaGoal(METRIKA_GOALS.authLoginSuccess, {
      method,
      new_user: isNewUserRef.current,
      place: authContextRef.current.place || authContextRef.current.kind || "login",
    });
  };

  // Вход из «Сохранить расчёт» на лендинге ведёт в калькулятор сервиса, где расчёт подставится
  // (без компании калькулятор предложит её создать). Остальные входы — на главную.
  const getAfterLoginPath = () =>
    (authContextRef.current.kind === "save" && getPendingPublicCalcPath()) || AUTH_REDIRECT_PATHS.dashboard;

  const isPhoneReady = useMemo(
    () => Boolean(pohoneNumber && isValidPhoneNumber(pohoneNumber)),
    [pohoneNumber]
  );

  const phoneStateClass = useMemo(() => {
    if (!pohoneNumber) return "is-empty";
    return isPossiblePhoneNumber(pohoneNumber) ? "is-valid" : "is-warning";
  }, [pohoneNumber]);

  // Свой номер показываем целиком: человек сверит, что ввёл его без ошибки.
  const typedPhone = useMemo(() => formatPhoneForCall(pohoneNumber), [pohoneNumber]);

  const applySystemTheme = (nextIsDark = getSystemPrefersDarkTheme()) => {
    setIsDark(nextIsDark);
    applyAuthThemeToDocument(nextIsDark);
  };
  useEffect(() => {
  captureReferralCodeFromLocation();
}, []);

  useEffect(() => {
    const mediaQuery =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia(SYSTEM_DARK_THEME_QUERY)
        : null;

    const syncSystemTheme = (event) => {
      const nextIsDark =
        typeof event?.matches === "boolean" ? event.matches : Boolean(mediaQuery?.matches);

      applySystemTheme(nextIsDark);
    };

    syncSystemTheme();

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener("change", syncSystemTheme);
    } else if (mediaQuery?.addListener) {
      mediaQuery.addListener(syncSystemTheme);
    }

    return () => {
      if (mediaQuery?.removeEventListener) {
        mediaQuery.removeEventListener("change", syncSystemTheme);
      } else if (mediaQuery?.removeListener) {
        mediaQuery.removeListener(syncSystemTheme);
      }

      document.body.classList.remove("uprasti-auth-dark");
      document.body.classList.remove("dark");
      document.documentElement.setAttribute("data-theme", "light");
      setAuthAutoDarkCss(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver !== "function") {
      return undefined;
    }

    const cards = Array.from(document.querySelectorAll(".landingRevealOnce"));
    if (!cards.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isSending) return;

    setInpDisapled(true);
    setloading(true);

    const params = new URLSearchParams();
    const token = new URLSearchParams(window.location.search).get("token");
    if (token) params.append("token", token);

    axios
      .post(`/pincode/v3/api?${params.toString()}`, {
        pinCode: pincodeToSendRef.current,
      })
      .then(async (response) => {
        const userId = response.data?.datas?._id;

        if (userId) {
          socket.emit("pushmyid", userId);
        }

        if (response.data?.authorization) {
          trackLoginSuccess("pin");
          setIsSending(false);
          setIsRedirecting(true);
          setInpDisapled(true);
          setloading(true);

          await notifySocketBeforeRedirect(userId);

          window.setTimeout(() => {
            window.location.replace(getAfterLoginPath());
          }, 450);

          return;
        }

        setIsSending(false);
        setIsRedirecting(false);
        setInpDisapled(false);
        setloading(false);

        toast({
          title: response.data?.message || "Проверка принята",
          description: "Проверяем вход и готовим переход в кабинет.",
          status: "info",
          duration: 2200,
          isClosable: true,
        });
      })
      .catch((error) => {
        console.log(error);
        setIsSending(false);
        setIsRedirecting(false);
        setInpDisapled(false);
        setloading(false);
        setinptValue("");
        pincodeToSendRef.current = "";

        setTimeout(() => {
          focusCodeInput();
        }, 120);

        setAuthError(error.response?.data?.message || "Код не подошёл. Введите 4 цифры ещё раз.");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSending]);

  useEffect(() => {
    async function unsubscribeCurrentDevice() {
      const deviceId = localStorage.getItem("deviceId");
      if (!deviceId) return;
      await axios.post("/unsubscribe", { deviceId }, { withCredentials: true });
    }

    unsubscribeCurrentDevice().catch((error) =>
      console.warn("unsubscribeCurrentDevice failed:", error?.message || error)
    );
    axios.get("/registration/v2/api").catch((error) => console.log(error));
  }, []);

  useEffect(() => {
    if (!isCodeStep) return;

    const timer = setTimeout(() => {
      focusRefDesktop.current?.focus();
    }, 220);

    return () => clearTimeout(timer);
  }, [isCodeStep]);

  useEffect(() => {
    let cancelled = false;

    const applyCountry = (nextCountry) => {
      const normalizedCountry = normalizeCountryCode(nextCountry);

      if (!cancelled && normalizedCountry) {
        setcountry(normalizedCountry);
      }
    };

    applyCountry(guessPhoneCountry());

    const fetchData = async () => {
      const serverCountry = await detectPhoneCountryFromServer();

      if (serverCountry) {
        applyCountry(serverCountry);
        return;
      }

      const publicCountry = await detectPhoneCountryFromPublicApi();

      if (publicCountry) {
        applyCountry(publicCountry);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, []);

  const focusCodeInput = () => {
    focusRefDesktop.current?.focus();
  };

  const handelPincode = (value) => {
    const nextValue = String(value || "").replace(/\D/g, "").slice(0, 4);

    if (inpDisapled || loading || isSending || isRedirecting) return;

    pincodeToSendRef.current = nextValue;
    setinptValue(nextValue);

    if (nextValue.length === 4) {
      setIsSending(true);
      setInpDisapled(true);
    }
  };

  // newNumber: человек сам просит другой номер для звонка (прежний не сработал), сервер не отдаст старый.
  const handleCall = ({ newNumber = false } = {}) => {
    if (!isPhoneReady) {
      setAuthError(
        pohoneNumber
          ? "Проверьте номер: похоже, в нём не хватает цифр или есть лишние."
          : "Впишите номер телефона."
      );
      return;
    }

    const normalizedPhone = normalizePhoneForServer(pohoneNumber, country);

    trackMetrikaGoal(METRIKA_GOALS.authCodeRequested, {
      place: authContextRef.current.place || authContextRef.current.kind || "login",
    });

    const previousCallPhone = newNumber ? normalizeCallPhone(outgoingAuth?.callPhone) : "";

    setIsRedirecting(false);
    setOutgoingAuth(null);
    setOutgoingStatusText("");
    setIsLocalPinMode(false);
    setAuthError("");
    setCallEnded("");
    setloading(true);
    setInpDisapled(true);

    axios
      .post(
        "/registration/v3/api",
        appendAttributionToPayload(
          appendReferralCodeToPayload({
            tel: normalizedPhone,
            phone: normalizedPhone,
            country,
            ...(newNumber ? { newNumber: true } : {}),
          })
        ),
        { withCredentials: true }
      )
      .then((response) => {
        const data = response.data || {};

        // 201 — номера ещё не было в базе, создан новый пользователь.
        isNewUserRef.current = response.status === 201 || data.newUser === true;
        if (isNewUserRef.current) {
          trackMetrikaGoal(METRIKA_GOALS.registrationNewUser);
        }

        setloading(false);
        setInpDisapled(false);
        setinptValue("");
        pincodeToSendRef.current = "";

        if (data.mode === "outgoing_call" || data.status === "waiting_call" || data.callPhone) {
          setIsCodeStep(false);
          setOutgoingAuth({
            phone: normalizedPhone,
            callPhone: data.callPhone,
            expiresAt: data.expiresAt,
            attemptToken: data.attemptToken,
            startedAt: Date.now(),
            // Plusofon может дать те же цифры на новую попытку: скажем, что звонить снова туда же.
            sameNumber: Boolean(previousCallPhone) && previousCallPhone === normalizeCallPhone(data.callPhone),
          });
          setOutgoingStatusText("");
          return;
        }

        setIsLocalPinMode(data.mode === "pin" || data.nextStep === "pin");
        setIsCodeStep(true);
      })
      .catch((error) => {
        setIsCodeStep(false);
        setOutgoingAuth(null);
        setOutgoingStatusText("");
        setIsLocalPinMode(false);
        setloading(false);
        setInpDisapled(false);
        setAuthError(
          error.response?.data?.message
            ? `${error.response.data.message}. Попробуйте ещё раз.`
            : "Не получилось начать вход. Проверьте интернет и нажмите «Продолжить» ещё раз."
        );
      });
  };


  useEffect(() => {
    if (!outgoingAuth?.callPhone) return undefined;

    let cancelled = false;
    // Один запрос за раз. Когда человек возвращается из звонилки, браузер на телефоне выпускает
    // отложенные проверки разом: первая впускала, вторая уже не находила попытку и показывала
    // «Вход не завершился», а иногда её ответ приходил раньше и вход терялся (23.10).
    let inFlight = false;
    let failures = 0;

    const params = new URLSearchParams();
    const token = new URLSearchParams(window.location.search).get("token");
    if (token) params.append("token", token);
    // Токен попытки: вход найдётся, даже если cookie сессии сменилась после старта.
    const attemptHeaders = outgoingAuth.attemptToken
      ? { "X-Auth-Attempt": outgoingAuth.attemptToken }
      : {};

    const stop = () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", checkOnReturn);
      window.removeEventListener("focus", checkOnReturn);
    };

    const openCabinet = async (userId) => {
      trackLoginSuccess("call");

      if (userId) {
        socket.emit("pushmyid", userId);
      }

      setIsRedirecting(true);
      setInpDisapled(true);
      setloading(true);
      setOutgoingStatusText("");

      await notifySocketBeforeRedirect(userId);

      window.setTimeout(() => {
        window.location.replace(getAfterLoginPath());
      }, 450);
    };

    // Сервер уже впустил этот браузер, а ответ об этом потерялся: проверяем вход, прежде чем
    // просить звонить заново.
    const findSignedInUser = async () => {
      try {
        const { data } = await axios.get("/registration/v2/api", { withCredentials: true });
        return data?.authorization ? data.data || {} : null;
      } catch (error) {
        return null;
      }
    };

    const checkStatus = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;

      try {
        const response = await axios.get(
          `/registration/outgoing-call/status/api?${params.toString()}`,
          { withCredentials: true, headers: attemptHeaders }
        );

        if (cancelled) return;

        failures = 0;
        const data = response.data || {};

        if (data.callPhone || data.expiresAt) {
          setOutgoingAuth((prev) => {
            if (!prev) return prev;

            return {
              ...prev,
              callPhone: data.callPhone || prev.callPhone,
              expiresAt: data.expiresAt || prev.expiresAt,
            };
          });
        }

        if (data.authorization) {
          stop();
          await openCabinet(data.datas?._id);
          return;
        }

        // Сервер не видит попытку входа: опрос дальше бесполезен, просим начать заново,
        // а не держим вечное «Ждём ваш звонок…».
        if (data.status === "not_found" || data.status === "not_started") {
          stop();
          const signedInUser = await findSignedInUser();
          if (signedInUser) {
            await openCabinet(signedInUser._id);
            return;
          }
          setloading(false);
          setInpDisapled(false);
          setOutgoingAuth(null);
          setOutgoingStatusText("");
          setAuthError("Вход не завершился. Нажмите «Продолжить» ещё раз, покажем новый номер.");
          return;
        }

        // Время вышло или звонок не засчитан: опрос останавливаем (раньше он продолжался
        // и каждые 10 секунд показывал ту же ошибку), в карточке кнопка «Получить новый номер».
        if (data.status === "expired" || data.status === "rejected") {
          stop();
          setloading(false);
          setInpDisapled(false);
          setCallEnded(data.status === "expired" ? "Время на звонок вышло" : "Звонок не засчитан");
          return;
        }

        setOutgoingStatusText("");
      } catch (error) {
        if (cancelled) return;
        failures += 1;
        // Пока человек в звонилке, телефон обрывает запросы страницы: это не ошибка связи.
        if (!document.hidden && failures >= 2) {
          setOutgoingStatusText("Нет связи с сервером, пробуем ещё раз…");
        }
      } finally {
        inFlight = false;
      }
    };

    // Вернулся из звонилки: проверяем сразу, не дожидаясь следующего круга.
    function checkOnReturn() {
      if (!document.hidden) checkStatus();
    }

    // Статус попытки сервер держит в памяти, вызов дешёвый: проверяем часто, чтобы после
    // звонка человек не ждал до 10 секунд.
    const timer = window.setInterval(checkStatus, 3000);
    document.addEventListener("visibilitychange", checkOnReturn);
    window.addEventListener("focus", checkOnReturn);
    checkStatus();

    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoingAuth?.startedAt]);

  // Через полминуты ожидания предлагаем другой номер: звонок мог не дойти или не засчитаться.
  useEffect(() => {
    setOfferNewNumber(false);
    if (!outgoingAuth?.startedAt) return undefined;

    const timer = window.setTimeout(() => setOfferNewNumber(true), 30000);
    return () => window.clearTimeout(timer);
  }, [outgoingAuth?.startedAt]);

  const sendQrCodeToServer = async (qrCode) => {
    setloading(true);

    try {
      const response = await axios.post("/qrCodeAutorizationBySocket/v3/api", {
        qrCode,
      });

      const userId = response.data?.datas?._id;

      if (userId) {
        socket.emit("pushmyid", userId);
      }

      if (response.data?.authorization) {
        await notifySocketBeforeRedirect(userId);
        window.location.replace(getAfterLoginPath());
      }
    } catch (error) {
      console.error("Ошибка при отправке запроса:", error);
      toast({
        title: error.response?.data?.message || "Ошибка",
        description: error.response?.data?.message || "Ошибка авторизации по QR-коду",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    function sendForServer(msg, myinfo) {
      sendQrCodeToServer(myinfo._id);
    }

    socket.on("sendMessageSocket", sendForServer);
    return () => socket.off("sendMessageSocket", sendForServer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToPhone = () => {
    setAuthError("");
    setCallEnded("");
    setIsCodeStep(false);
    setOutgoingAuth(null);
    setOutgoingStatusText("");
    setIsLocalPinMode(false);
    setinptValue("");
    pincodeToSendRef.current = "";
    setInpDisapled(false);
    setloading(false);
    setIsRedirecting(false);
  };


  const isPinChecking = isCodeStep && (loading || isSending || inpDisapled || isRedirecting);

  const renderError = () =>
    authError ? (
      <p className={styles.error} role="alert">
        {authError}
      </p>
    ) : null;

  const renderLegal = () => (
    <p className={styles.legal}>
      Нажимая «Продолжить», вы соглашаетесь с{" "}
      <OfferModal nameText="условиями сервиса" textInModal={LEGAL_DOCUMENTS.offer} />,{" "}
      <OfferModal nameText="политикой конфиденциальности" textInModal={LEGAL_DOCUMENTS.privacy} /> и даёте{" "}
      <OfferModal
        nameText="согласие на обработку персональных данных"
        textInModal={LEGAL_DOCUMENTS.consent}
      />
      .
    </p>
  );

  // Шаг 1: только номер. Плашка «Ваши данные в безопасности» и вход по QR-коду для новичка
  // лишние: QR остаётся тем, кто открыл окно кнопкой «Войти».
  const renderPhoneStep = () => {
    // Читаем при каждой отрисовке: окно рисует лендинг, Outh при открытии не перерисовывается.
    const authKind = authContextRef.current.kind || "signin";
    const text = phoneStepText(authKind);
    return (
      <>
        <h2 className={styles.title}>{text.title}</h2>
        <p className={styles.lede}>{text.lede}</p>

        <p className={styles.label} id="auth-phone-label">
          Номер телефона
        </p>
        <PhoneInput
          key={`login-phone-${country}`}
          ref={inputRefDesktop}
          initialValueFormat="national"
          labels={ru}
          className={`verifyInput ${phoneStateClass}`}
          international
          defaultCountry={country}
          limitMaxLength
          placeholder="Номер телефона"
          value={pohoneNumber}
          onChange={(value) => {
            setPohoneNumber(value);
            if (authError) setAuthError("");
          }}
          numberInputProps={{
            inputMode: "tel",
            autoComplete: "tel",
            enterKeyHint: "send",
            "aria-labelledby": "auth-phone-label",
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !inpDisapled) handleCall();
          }}
        />
        <p className={styles.hint}>Номер нужен только для входа, его никто не увидит.</p>

        {renderError()}

        <button type="button" className={styles.primary} onClick={() => handleCall()} disabled={inpDisapled}>
          {loading ? "Готовим номер…" : "Продолжить"}
        </button>
        <p className={styles.next}>
          Дальше покажем номер. Позвоните на него с этого телефона, и вход подтвердится сам.
        </p>

        {renderLegal()}

        {authKind === "signin" ? (
          <div className={styles.qr}>
            <LendingQrScannModalBTN variant="compact" isDarkMode={isDark} />
            <span>
              <b>Войти по QR-коду</b>
              если Упрости уже открыт на другом устройстве
            </span>
          </div>
        ) : null}
      </>
    );
  };

  // Шаг 2: позвонить на номер. Всё в карточке, без всплывающих окон поверх неё.
  const renderOutgoingCallStep = () => {
    const callPhone = formatPhoneForCall(outgoingAuth?.callPhone);
    const callPhoneHref = normalizeCallPhone(outgoingAuth?.callPhone);
    const windowText = callWindowText(outgoingAuth?.expiresAt, outgoingAuth?.startedAt);

    if (isRedirecting) {
      return (
        <>
          <h2 className={styles.title}>Вход подтверждён</h2>
          <p className={cx(styles.status, styles.done)} role="status">
            <span className={styles.spinner} aria-hidden="true" />
            Открываем личный кабинет…
          </p>
        </>
      );
    }

    return (
      <>
        <button type="button" className={styles.back} onClick={handleBackToPhone}>
          <ArrowLeftOutlined aria-hidden="true" />
          Изменить мой телефон
        </button>

        <h2 className={styles.title}>{callEnded ? callEnded : "Позвоните на этот номер"}</h2>
        <p className={styles.lede}>
          {callEnded ? "Нажмите кнопку, и мы покажем новый номер. Звоните, как и раньше, с телефона" : "Звоните с телефона"}{" "}
          <b className={styles.nowrap}>{typedPhone}</b>.{callEnded ? "" : " Вход подтвердится сам."}
        </p>

        {callEnded ? (
          <>
            {renderError()}
            <button type="button" className={styles.primary} onClick={() => handleCall({ newNumber: true })} disabled={loading}>
              {loading ? "Готовим номер…" : "Получить новый номер"}
            </button>
          </>
        ) : (
          <>
            <div className={styles.callBox}>
              <span className={styles.callLabel}>Номер для звонка</span>
              <strong className={styles.callNumber}>{callPhone}</strong>
              {outgoingAuth?.sameNumber ? (
                <span className={styles.callNote}>Номер тот же, позвоните на него ещё раз.</span>
              ) : null}
              {windowText ? <span className={styles.callNote}>{windowText}</span> : null}
            </div>

            {callPhoneHref ? (
              <a className={cx(styles.primary, styles.touchOnly)} href={`tel:${callPhoneHref}`}>
                <PhoneOutlined aria-hidden="true" />
                Позвонить
              </a>
            ) : null}

            <p className={styles.status} role="status" aria-live="polite">
              <span className={styles.spinner} aria-hidden="true" />
              {outgoingStatusText || "Ждём ваш звонок…"}
            </p>
            <p className={styles.next}>
              Звонок сбросится сам, разговаривать не нужно. Эту страницу не закрывайте: после звонка она
              откроет кабинет сама.
            </p>
            {offerNewNumber ? (
              <p className={styles.next}>
                Позвонили, а кабинет не открылся?{" "}
                <button type="button" className={styles.inlineLink} onClick={() => handleCall({ newNumber: true })}>
                  Получить другой номер
                </button>
              </p>
            ) : null}
          </>
        )}
      </>
    );
  };

  // Запасной шаг: 4 цифры входящего звонка (на тестовом сервере код 1234).
  const renderCodeStep = () => (
    <>
      <button type="button" className={styles.back} onClick={handleBackToPhone} disabled={isPinChecking}>
        <ArrowLeftOutlined aria-hidden="true" />
        Изменить мой телефон
      </button>

      <h2 className={styles.title}>{isRedirecting ? "Вход подтверждён" : "Введите 4 цифры"}</h2>
      <p className={styles.lede}>
        {isLocalPinMode ? (
          "Тестовый вход: введите 1234."
        ) : (
          <>
            Сейчас на <b className={styles.nowrap}>{typedPhone}</b> позвонят. Трубку брать не нужно:
            введите 4 цифры номера, который звонит.
          </>
        )}
      </p>

      <div className={`authPinArea ${styles.pin}`}>
        <HStack className="authPinStack">
          <PinInput
            type="number"
            otp
            value={inptValue}
            onChange={handelPincode}
            isDisabled={isPinChecking}
            variant="flushed"
            isRequired
          >
            <PinInputField ref={focusRefDesktop} inputMode="numeric" autoComplete="one-time-code" aria-label="Цифра 1" />
            <PinInputField inputMode="numeric" aria-label="Цифра 2" />
            <PinInputField inputMode="numeric" aria-label="Цифра 3" />
            <PinInputField inputMode="numeric" aria-label="Цифра 4" />
          </PinInput>
        </HStack>
      </div>

      {renderError()}

      {isPinChecking ? (
        <p className={cx(styles.status, isRedirecting && styles.done)} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {isRedirecting ? "Открываем личный кабинет…" : "Проверяем цифры…"}
        </p>
      ) : (
        <p className={styles.next}>Как только введёте четвёртую цифру, вход выполнится сам.</p>
      )}
    </>
  );

  const renderAuthCard = () => (
    <div className={styles.card} id="auth">
      {outgoingAuth ? renderOutgoingCallStep() : isCodeStep ? renderCodeStep() : renderPhoneStep()}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Упрости — AI-рекомендации, юнит-экономика и P&L для селлеров WB и Ozon</title>
        <meta
          name="description"
          content="AI-рекомендации по каждому товару, юнит-экономика, P&L, остатки, отзывы и бесплатный калькулятор прибыли для селлеров Wildberries и Ozon."
        />
      </Helmet>

      <Landing
        isDark={isDark}
        logo={logosvg}
        legal={LEGAL_INFO}
        shots={LANDING_SHOTS}
        renderAuthCard={renderAuthCard}
        onAuthOpened={(context) => {
          authContextRef.current = context || {};
        }}
      />
    </>
  );
}
